<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import type { Ref } from 'vue';

let showLeftBlur: Ref<boolean> = ref(true);
let showRightBlur: Ref<boolean> = ref(false);
const chartRef = ref<Element>();

const props = withDefaults(
  defineProps<{
    scrollDirection?: 'ltr' | 'rtl';
    // (async) parent data to watch for and update the indicator on change
    watchTarget?: unknown;
  }>(),
  {
    scrollDirection: 'ltr',
  },
);

async function updateScrollIndicator() {
  // Wait for component render update before accessing DOM values
  await nextTick();

  if (!chartRef?.value) return;

  const { scrollLeft, scrollWidth, clientWidth } = chartRef.value;

  props.scrollDirection === 'ltr'
    ? handleForLeftToRight(scrollLeft, scrollWidth, clientWidth)
    : handleForRightToLeft(scrollLeft, scrollWidth, clientWidth);
}

function handleForRightToLeft(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
) {
  const maxLeftScroll = (scrollWidth - clientWidth) * -1;
  showLeftBlur.value = scrollLeft > maxLeftScroll;
  showRightBlur.value = scrollLeft !== 0;
}

function handleForLeftToRight(
  scrollLeft: number,
  scrollWidth: number,
  clientWidth: number,
) {
  const maxRightScroll = scrollWidth - clientWidth;
  showLeftBlur.value = scrollLeft !== 0;
  showRightBlur.value = scrollLeft < maxRightScroll;
}

watch(
  () => props.watchTarget,
  async () => {
    await updateScrollIndicator();
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div class="relative">
    <div
      v-if="showLeftBlur"
      class="pointer-events-none absolute top-0 h-full w-7 bg-gradient-to-r from-slate-200 to-transparent z-20 rounded-l-xl"
    />
    <div
      v-if="showRightBlur"
      class="pointer-events-none absolute right-0 top-0 h-full w-7 bg-gradient-to-l from-slate-200 to-transparent z-20 rounded-r-xl"
    />

    <div ref="chartRef" class="overflow-x-auto" :dir="props.scrollDirection" @scroll="updateScrollIndicator" style="max-width: 98%">
      <slot/>
    </div>
  </div>
</template>

<style scoped>

</style>